import {
  DocumentKeyplanDTO,
  EditorDataFormKeylabel,
  UpdateEditorDataFormKeyLabel,
  UpdateEditorDataFormText,
} from "interfaces/models/documentKeyplan";
import { KeyplanLayoutDTO } from "interfaces/models/keyplanLayout";
import { set } from "lodash-es";
import AbstractStore from "stores/AbstractStore";
import { uuid } from "utils/common";
import { defaultState, KeyplanAction, KeyplanState } from "./type";

// Create a class to manage the keyplan store
class KeyplanStore extends AbstractStore<KeyplanState, KeyplanAction> {
  // Constructor can accept initial state (optional)
  constructor(initialState?: Partial<KeyplanState>) {
    const mergedState = initialState
      ? { ...defaultState, ...initialState }
      : defaultState;
    super(mergedState);
  }

  dispatch = (action: KeyplanAction): void => {
    switch (action.type) {
      case "FETCHING_KEYPLAN_LAYOUT": {
        this.setState({
          ...this.state,
          isFetchingKeyplanLayout: action.payload,
        });
        break;
      }

      case "FETCHING_DOCUMENT_KEYPLANS": {
        this.setState({
          ...this.state,
          isFetchingDocumentKeyplans: action.payload,
        });
        break;
      }

      case "SET_URN": {
        this.setState({ ...this.state, urn: action.payload });
        break;
      }

      case "SET_DOCUMENT_ID": {
        this.setState({ ...this.state, documentId: action.payload });
        break;
      }

      case "SET_DOCUMENT_FORMAT_ID": {
        this.setState({ ...this.state, documentFormatId: action.payload });
        break;
      }

      case "SET_KEYPLAN_LAYOUT": {
        this.setState({ ...this.state, keyplanLayout: action.payload });
        break;
      }

      case "SET_DOCUMENT_KEYPLANS": {
        if (!action.payload) {
          this.setState({ ...this.state, mapDocumentKeyplan: null });
          break;
        }

        const mapDocumentKeyplan = new Map<string, DocumentKeyplanDTO>();
        action.payload.forEach((item) => {
          mapDocumentKeyplan.set(item.id!, item);
        });
        this.setState({ ...this.state, mapDocumentKeyplan });

        break;
      }

      case "SET_IS_FETCHING_BIM_VIEWS": {
        this.setState({ ...this.state, isFetchingBimViews: action.payload });
        break;
      }

      case "SET_BIM_VIEWS": {
        this.setState({ ...this.state, bimViews: action.payload });
        break;
      }

      case "RESET_STATE": {
        this.setState({ ...defaultState });
        break;
      }

      case "ADD_TEMP_DOCUMENT_KEYPLAN": {
        const mapDocumentKeyplan = new Map(
          this.state.mapDocumentKeyplan || new Map()
        );
        const id = uuid();
        mapDocumentKeyplan.set(id, { ...action.payload, id });
        this.setState({ ...this.state, mapDocumentKeyplan });

        break;
      }

      case "REMOVE_TEMP_DOCUMENT_KEYPLAN": {
        const mapDocumentKeyplan = new Map(
          this.state.mapDocumentKeyplan || new Map()
        );
        mapDocumentKeyplan.delete(action.payload);
        this.setState({ ...this.state, mapDocumentKeyplan });

        break;
      }

      case "SET_IS_EDIT_MODE": {
        this.setState({ ...this.state, isEditMode: action.payload });

        break;
      }

      case "UPDATE_DOCUMENT_KEYPLAN": {
        const mapDocumentKeyplan = new Map(
          this.state.mapDocumentKeyplan || new Map()
        );
        const documentKeyplan = mapDocumentKeyplan.get(action.payload.id);
        if (!documentKeyplan) {
          break;
        }

        mapDocumentKeyplan.set(action.payload.id, {
          ...documentKeyplan,
          ...action.payload,
        });
        this.setState({ ...this.state, mapDocumentKeyplan });

        break;
      }

      case "FIXED_KEYPLAN": {
        this.setState({ ...this.state, isFixed: action.payload });

        break;
      }

      case "SET_RESOURCE": {
        this.setState({ ...this.state, resource: action.payload });
        break;
      }

      case "UPDATE_TEMP_EDITOR_FORM_TEXT": {
        const mapTempEditorDataFormText = new Map(
          this.state.mapTempFormText ||
            new Map<string, Record<string, UpdateEditorDataFormText>>()
        );
        const mapFormText =
          mapTempEditorDataFormText.get(action.payload.documentKeyplanId) ||
          ({} as Record<string, UpdateEditorDataFormText>);
        if (action.payload.data) {
          set(mapFormText, `${action.payload.frameId}`, action.payload);
        } else {
          delete mapFormText?.[action.payload.frameId];
        }
        mapTempEditorDataFormText.set(
          action.payload.documentKeyplanId,
          mapFormText
        );
        this.setState({
          ...this.state,
          mapTempFormText: mapTempEditorDataFormText,
        });

        break;
      }

      case "UPDATE_EDITOR_FORM_TEXT_FROM_TEMP": {
        const mapDocumentKeyplan = new Map(
          this.state.mapDocumentKeyplan || new Map<string, DocumentKeyplanDTO>()
        );
        const mapTempEditorDataFormText = new Map(this.state.mapTempFormText);
        const documentKeyplan = mapDocumentKeyplan.get(action.payload);
        const mapFormText = mapTempEditorDataFormText.get(action.payload);
        if (!documentKeyplan || !mapFormText) {
          break;
        }

        Object.values(mapFormText).forEach((formText) => {
          set(
            documentKeyplan,
            ["editorData", "mapFormText", `${formText.frameId}`],
            formText.data
          );
        });

        mapTempEditorDataFormText.delete(action.payload);
        mapDocumentKeyplan.set(action.payload, documentKeyplan);
        this.setState({
          ...this.state,
          mapDocumentKeyplan,
          mapTempFormText: mapTempEditorDataFormText,
        });

        break;
      }

      case "UPDATE_EDITOR_FORM_TEXT": {
        const mapDocumentKeyplan = new Map(
          this.state.mapDocumentKeyplan || new Map<string, DocumentKeyplanDTO>()
        );
        const documentKeyplan = mapDocumentKeyplan.get(
          action.payload.documentKeyplanId
        );
        if (!documentKeyplan) {
          break;
        }

        set(
          documentKeyplan,
          ["editorData", "mapFormText", `${action.payload.frameId}`],
          action.payload.data
        );

        mapDocumentKeyplan.set(
          action.payload.documentKeyplanId,
          documentKeyplan
        );
        this.setState({
          ...this.state,
          mapDocumentKeyplan,
        });

        break;
      }

      case "CLEAR_TEMP_EDITOR_FORM": {
        this.setState({
          ...this.state,
          mapTempFormText: new Map(),
          mapTempFormKeyLabel: new Map(),
        });

        break;
      }

      case "UPDATE_TEMP_EDITOR_FORM_KEYLABEL": {
        const mapTempEditorDataFormKeyLabel = new Map(
          this.state.mapTempFormKeyLabel ||
            new Map<string, Record<string, UpdateEditorDataFormKeyLabel>>()
        );

        const mapFormKeyLabel =
          mapTempEditorDataFormKeyLabel.get(action.payload.documentKeyplanId) ||
          ({} as Record<string, UpdateEditorDataFormKeyLabel>);
        if (action.payload.data) {
          set(mapFormKeyLabel, `${action.payload.pinId}`, action.payload);
        } else {
          delete mapFormKeyLabel?.[action.payload.pinId];
        }

        mapTempEditorDataFormKeyLabel.set(
          action.payload.documentKeyplanId,
          mapFormKeyLabel
        );
        this.setState({
          ...this.state,
          mapTempFormKeyLabel: mapTempEditorDataFormKeyLabel,
        });

        break;
      }

      case "UPDATE_EDITOR_FORM_KEYLABEL_FROM_TEMP": {
        const mapDocumentKeyplan = new Map(
          this.state.mapDocumentKeyplan || new Map<string, DocumentKeyplanDTO>()
        );
        const mapTempEditorDataFormKeyLabel = new Map(
          this.state.mapTempFormKeyLabel
        );
        const documentKeyplan = mapDocumentKeyplan.get(action.payload);
        const mapFormKeyLabel = mapTempEditorDataFormKeyLabel.get(
          action.payload
        );

        if (!documentKeyplan || !mapFormKeyLabel) {
          break;
        }

        Object.values(mapFormKeyLabel).forEach((formText) => {
          set(
            documentKeyplan,
            ["editorData", "mapFormKeylabel", `${formText.pinId}`],
            formText.data
          );
        });

        mapTempEditorDataFormKeyLabel.delete(action.payload);
        mapDocumentKeyplan.set(action.payload, documentKeyplan);

        this.setState({
          ...this.state,
          mapDocumentKeyplan,
          mapTempFormKeyLabel: mapTempEditorDataFormKeyLabel,
        });

        break;
      }

      case "TOGGLE_CHANGE_ROTATE_MODE": {
        this.setState({ ...this.state, isRotateMode: action.payload });

        break;
      }

      case "SET_PAGE_RATIO_EDIT_MODE": {
        this.setState({ ...this.state, pageRatioEditMode: action.payload });

        break;
      }

      default:
        // eslint-disable-next-line
        console.error("Unknown action type");
    }
  };

  fetchingKeyplanLayout = (isFetching: boolean): void => {
    this.dispatch({ type: "FETCHING_KEYPLAN_LAYOUT", payload: isFetching });
  };

  fetchingDocumentKeyplans = (isFetching: boolean): void => {
    this.dispatch({ type: "FETCHING_DOCUMENT_KEYPLANS", payload: isFetching });
  };

  setUrn = (urn: string | null | undefined): void => {
    this.dispatch({ type: "SET_URN", payload: urn });
  };

  setDocumentFormatId = (id: KeyplanState["documentFormatId"]): void => {
    this.dispatch({ type: "SET_DOCUMENT_FORMAT_ID", payload: id });
  };

  setDocumentId = (id: KeyplanState["documentId"]): void => {
    this.dispatch({ type: "SET_DOCUMENT_ID", payload: id });
  };

  setKeyplanLayout = (
    keyplanLayout: KeyplanState["keyplanLayout"]
  ): KeyplanLayoutDTO | null => {
    this.dispatch({ type: "SET_KEYPLAN_LAYOUT", payload: keyplanLayout });

    return this.state.keyplanLayout;
  };

  setDocumentKeyplans = (
    documentKeyplans: DocumentKeyplanDTO[] | undefined
  ): void => {
    this.dispatch({ type: "SET_DOCUMENT_KEYPLANS", payload: documentKeyplans });
  };

  setBimViews = (bimViews: KeyplanState["bimViews"]): void => {
    this.dispatch({ type: "SET_BIM_VIEWS", payload: bimViews });
  };

  setIsFetchingBimViews = (isFetching: boolean): void => {
    this.dispatch({ type: "SET_IS_FETCHING_BIM_VIEWS", payload: isFetching });
  };

  addTempDocumentKeyplan = (item: Partial<DocumentKeyplanDTO>): void => {
    this.dispatch({ type: "ADD_TEMP_DOCUMENT_KEYPLAN", payload: item });
  };

  removeTempDocumentKeyplan = (id: string): void => {
    this.dispatch({ type: "REMOVE_TEMP_DOCUMENT_KEYPLAN", payload: id });
  };

  setIsEditMode = (isEdit: boolean): void => {
    this.dispatch({ type: "SET_IS_EDIT_MODE", payload: isEdit });
  };

  updateDocumentKeyplan = (
    data: Partial<DocumentKeyplanDTO> & { id: string }
  ): Map<string, DocumentKeyplanDTO> => {
    this.dispatch({ type: "UPDATE_DOCUMENT_KEYPLAN", payload: data });
    const result = new Map(this.state.mapDocumentKeyplan || new Map());

    return result;
  };

  fixedKeyplan = (isFixed: boolean): void => {
    this.dispatch({ type: "FIXED_KEYPLAN", payload: isFixed });
  };

  setResource = (resource: KeyplanState["resource"]): void => {
    this.dispatch({ type: "SET_RESOURCE", payload: resource });
  };

  updateEditorFormKeylabel = (payload: {
    documentKeyplanId: string;
    pinId: string;
    data: EditorDataFormKeylabel;
  }): void => {
    this.dispatch({ type: "UPDATE_EDITOR_FORM_KEYLABEL", payload });
  };

  updateTempEditorFormText = (payload: UpdateEditorDataFormText): void => {
    this.dispatch({ type: "UPDATE_TEMP_EDITOR_FORM_TEXT", payload });
  };

  updateEditorFormTextFromTemp = (documentKeyplanId: string): void => {
    this.dispatch({
      type: "UPDATE_EDITOR_FORM_TEXT_FROM_TEMP",
      payload: documentKeyplanId,
    });
  };

  updateEditorFormText = (payload: UpdateEditorDataFormText): void => {
    this.dispatch({
      type: "UPDATE_EDITOR_FORM_TEXT",
      payload,
    });
  };

  clearTempEditorForm = (): void => {
    this.dispatch({ type: "CLEAR_TEMP_EDITOR_FORM" });
  };

  updateTempEditorFormKeyLabel = (
    payload: UpdateEditorDataFormKeyLabel
  ): void => {
    this.dispatch({ type: "UPDATE_TEMP_EDITOR_FORM_KEYLABEL", payload });
  };

  updateEditorFormKeyLabelFromTemp = (documentKeyplanId: string): void => {
    this.dispatch({
      type: "UPDATE_EDITOR_FORM_KEYLABEL_FROM_TEMP",
      payload: documentKeyplanId,
    });
  };

  toggleChangeRotateMode = (isShow: boolean): void => {
    this.dispatch({ type: "TOGGLE_CHANGE_ROTATE_MODE", payload: isShow });
  };

  setPageRatioEditMode = (ratio: number): void => {
    this.dispatch({ type: "SET_PAGE_RATIO_EDIT_MODE", payload: ratio });
  };

  resetState = (): void => {
    this.dispatch({ type: "RESET_STATE" });
  };
}

// Create an instance of store to use throughout the application
export const keyplanStore = new KeyplanStore();
